import React, { useState } from 'react';
import styled from 'styled-components';
import { useAccount } from 'wagmi';
import { useTokenComments } from '../../hooks/useTokenComments';
import { sendTokenComment, type TokenComment } from '../../utils/tokenComments';
import { ZNSAddressDisplay } from '../shared/ZNSAddressDisplay';

interface TokenCommentsProps {
  tokenAddress: string;
}

const Container = styled.div`
  width: 100%;
`;

const Subtitle = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #71717a;
  margin: 0 0 16px 0;
`;

const AddCommentSection = styled.div`
  margin-bottom: 20px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  background: rgba(9, 9, 11, 0.5);
  border: 1px solid rgba(39, 39, 42, 0.8);
  border-radius: 4px;
  color: #e4e4e7;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(168, 85, 247, 0.5);
  }
  
  &::placeholder {
    color: #71717a;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
`;

const PostButton = styled.button`
  margin-top: 8px;
  padding: 8px 16px;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 4px;
  color: #a855f7;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(168, 85, 247, 0.2);
    border-color: rgba(168, 85, 247, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CommentCard = styled.div<{ $highlighted?: boolean }>`
  padding: 12px;
  background: ${props => props.$highlighted 
    ? 'rgba(168, 85, 247, 0.1)' 
    : 'rgba(24, 24, 27, 0.3)'};
  border: 1px solid ${props => props.$highlighted 
    ? 'rgba(168, 85, 247, 0.4)' 
    : 'rgba(39, 39, 42, 0.5)'};
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(24, 24, 27, 0.5);
    border-color: rgba(39, 39, 42, 0.8);
  }
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CommenterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #a1a1aa;
`;

const CommentTime = styled.div`
  font-size: 11px;
  color: #52525b;
`;

const CommentMessage = styled.div`
  font-size: 13px;
  color: #d4d4d8;
  line-height: 1.5;
  word-wrap: break-word;
  margin-bottom: 8px;
`;

const CommentFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  color: #71717a;
`;

const CommentNumber = styled.span`
  color: #a855f7;
  font-weight: 500;
  font-size: 11px;
  cursor: pointer;
  
  &:hover {
    color: #9333ea;
    text-decoration: underline;
  }
`;

const ReplyLink = styled.span`
  color: #a855f7;
  font-weight: 500;
  cursor: pointer;
  font-size: 11px;
  
  &:hover {
    text-decoration: underline;
    color: #9333ea;
  }
`;

const ReplyButton = styled.button`
  background: transparent;
  border: none;
  color: #a855f7;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #9333ea;
    text-decoration: underline;
  }
`;

const ReplyCount = styled.span`
  color: #71717a;
  font-size: 11px;
`;

const ReplyingToInfo = styled.div`
  padding: 6px 10px;
  background: rgba(168, 85, 247, 0.1);
  border-left: 2px solid rgba(168, 85, 247, 0.4);
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 11px;
  color: #a1a1aa;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CancelReply = styled.button`
  background: transparent;
  border: none;
  color: #71717a;
  cursor: pointer;
  font-size: 11px;
  
  &:hover {
    color: #a1a1aa;
  }
`;

const NoComments = styled.div`
  text-align: center;
  color: #71717a;
  font-size: 12px;
  padding: 24px 20px;
  font-style: italic;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #71717a;
  font-size: 12px;
  padding: 24px 20px;
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 13px;
  margin-top: 8px;
`;

const SuccessText = styled.div`
  color: #10b981;
  font-size: 13px;
  margin-top: 8px;
`;

const TokenComments: React.FC<TokenCommentsProps> = ({ tokenAddress }) => {
  const { address, isConnected } = useAccount();
  const { comments, loading, refetch } = useTokenComments(tokenAddress);
  
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [postSuccess, setPostSuccess] = useState(false);
  const [replyingTo, setReplyingTo] = useState<TokenComment | null>(null);
  const [highlightedComment, setHighlightedComment] = useState<string | null>(null);

  const handlePostComment = async () => {
    if (!address || !newComment.trim()) return;

    setPosting(true);
    setPostError(null);
    setPostSuccess(false);

    try {
      // Save comment to Supabase (with parent if replying)
      await sendTokenComment(
        tokenAddress as `0x${string}`,
        newComment.trim(),
        address,
        replyingTo?.id
      );

      setNewComment('');
      setReplyingTo(null);
      setPostSuccess(true);
      setTimeout(() => setPostSuccess(false), 3000);
      
      // Refresh comments list
      refetch();
    } catch (err: any) {
      console.error('[TokenComments] Error posting:', err);
      setPostError(err.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleReplyClick = (comment: TokenComment) => {
    setReplyingTo(comment);
    // Scroll to text area
    window.scrollTo({ top: document.querySelector('textarea')?.offsetTop || 0, behavior: 'smooth' });
  };

  const handleCommentLinkClick = (commentId: string) => {
    setHighlightedComment(commentId);
    setTimeout(() => setHighlightedComment(null), 2000);
    
    // Scroll to comment
    const element = document.getElementById(`comment-${commentId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const getCommentNumber = (commentId: string) => {
    const index = comments.findIndex(c => c.id === commentId);
    return index !== -1 ? index + 1 : '?';
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Container>
      <Subtitle>
        Community discussion • Connect wallet to comment
      </Subtitle>

      {isConnected && (
        <AddCommentSection>
          {replyingTo && (
            <ReplyingToInfo>
              <span>
                Replying to <strong>No.{getCommentNumber(replyingTo.id)}</strong> by{' '}
                <ZNSAddressDisplay 
                  address={replyingTo.commenter_address}
                  truncateLength={4}
                  showIcon={false}
                  iconSize={12}
                />
              </span>
              <CancelReply onClick={() => setReplyingTo(null)}>✕ Cancel</CancelReply>
            </ReplyingToInfo>
          )}
          <TextArea
            placeholder={replyingTo 
              ? "Write your reply... (max 500 characters)" 
              : "Share your thoughts about this token... (max 500 characters)"}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            maxLength={500}
            disabled={posting}
          />
          <CharCount>{newComment.length}/500</CharCount>
          <PostButton
            onClick={handlePostComment}
            disabled={posting || !newComment.trim() || newComment.length > 500}
          >
            {posting ? 'Posting...' : (replyingTo ? '💬 Post Reply' : '📝 Post Comment')}
          </PostButton>
          {postError && <ErrorText>{postError}</ErrorText>}
          {postSuccess && <SuccessText>✅ Comment posted!</SuccessText>}
        </AddCommentSection>
      )}

      {!isConnected && (
        <AddCommentSection>
          <TextArea placeholder="Connect wallet to post comments..." disabled />
        </AddCommentSection>
      )}

      {loading && <LoadingText>Loading comments...</LoadingText>}

      {!loading && comments.length === 0 && (
        <NoComments>No comments yet. Be the first to comment!</NoComments>
      )}

      {!loading && comments.length > 0 && (
        <CommentsList>
          {comments.map((comment, index) => (
            <CommentCard 
              key={comment.id}
              id={`comment-${comment.id}`}
              $highlighted={highlightedComment === comment.id}
            >
              <CommentHeader>
                <CommenterInfo>
                  <CommentNumber onClick={() => handleReplyClick(comment)}>
                    No.{index + 1}
                  </CommentNumber>
                  <ZNSAddressDisplay 
                    address={comment.commenter_address}
                    truncateLength={4}
                    showIcon={true}
                    iconSize={14}
                  />
                </CommenterInfo>
                <CommentTime>{formatTime(comment.created_at)}</CommentTime>
              </CommentHeader>
              
              {comment.parent_comment_id && (
                <div style={{ marginBottom: '8px' }}>
                  <ReplyLink onClick={() => handleCommentLinkClick(comment.parent_comment_id!)}>
                    &gt;&gt;{getCommentNumber(comment.parent_comment_id)}
                  </ReplyLink>
                </div>
              )}
              
              <CommentMessage>{comment.message}</CommentMessage>
              
              <CommentFooter>
                <ReplyButton onClick={() => handleReplyClick(comment)}>
                  Reply
                </ReplyButton>
                {comment.reply_count > 0 && (
                  <ReplyCount>{comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}</ReplyCount>
                )}
              </CommentFooter>
            </CommentCard>
          ))}
        </CommentsList>
      )}
    </Container>
  );
};

export default TokenComments;

